import React, { useMemo, useState } from "react";
import { LoginFormProps } from "./LoginPage.types";
import { Container, Logo } from "./LoginPage.styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormModal } from "../../components/Modals";
import { getAuthTokens } from "../../services/authService/authService";
import { useAuth } from "../../context/AuthContext";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { getServerErrorData } from "utils/httpError";

const LoginPage: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const memoizedExternalErrors = useMemo(() => externalErrors, [externalErrors]);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useNotify();
  const { login } = useAuth();

  const fields = [
    {
      name: "email",
      type: "email",
      placeholder: t("auth.email"),
      required: true,
      width: "369px",
      validate: (value: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : t("auth.emailError"),
    },
    {
      name: "password",
      type: "password",
      width: "369px",
      placeholder: t("auth.password"),
      required: true,
      validate: (value: string) =>
        value.trim().length > 0 ? null : t("auth.passwordError"),
    },
  ];

  const footerLinks = [
    {
      text: t("auth.createAccount"),
      href: "/signup",
      variant: "primary",
    },
    {
      text: t("auth.passwordSos"),
      href: "/reset-password",
      variant: "secondary",
    },
  ];

  const handleSubmit = async (formData: Record<string, string>) => {
    setIsLoading(true);
    setExternalErrors({});
    try {
      await getAuthTokens(formData.email, formData.password);
      login();
      navigate("/main");
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors: Record<string, string> = {};

        for (const key in serverErrors) {
          if (serverErrors.hasOwnProperty(key)) {
            formattedErrors[key] = String(serverErrors[key]);
          }
        }

        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка авторизации:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка авторизации",
          life: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Logo>LISRENT</Logo>
      <FormModal
        title={t("auth.in")}
        fields={fields}
        onSubmit={handleSubmit}
        hasBackdrop={false}
        footerLinks={footerLinks}
        submitButtonText={t("auth.login")}
        externalErrors={memoizedExternalErrors}
        isLoading={isLoading}
      />
    </Container>
  );
};

export default LoginPage;
